import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useEffect } from "react";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { getEnvVar } from "../../utils/get-env";
import { usePayLaterEnabled } from "../paypal/hooks/usePayLaterEnabled";

/** @type {("checkout"|"cart"|"product-detail"|"product-listing")} */
const VALID_PAGE_TYPES = ["checkout", "cart", "product-detail", "product-listing"];

export const EPaypalApp = {
  MERCHANT: "merchant",
  PLATFORM: "platform",
};

export const PayPalPayButton = ({
  handleChangeStatus,
  onCaptureSuccess,
  orderId,
  pageType = "checkout",
  merchantId,
  appType = EPaypalApp.MERCHANT,
}) => {
  const { showSnackbar } = useGlobalSnackbar();

  // If appType is platform, or if paying the platform merchant account, force EPaypalApp.PLATFORM
  const resolvedAppType = appType === EPaypalApp.PLATFORM ? EPaypalApp.PLATFORM : EPaypalApp.MERCHANT;

  const { payLaterEnabled } = usePayLaterEnabled(merchantId);

  const paypalPartnerAttributionId = getEnvVar("REACT_APP_PAYPAL_PARTNER_ATTRIBUTION_ID") || "";
  const merchantClientId = getEnvVar("REACT_APP_PAYPAL_MERCHANT_CLIENT_ID");
  const platformClientId = getEnvVar("REACT_APP_PAYPAL_PLATFORM_CLIENT_ID");
  const clientId = (resolvedAppType === EPaypalApp.MERCHANT ? merchantClientId : platformClientId) || merchantClientId || platformClientId || "";

  const dataPageType = VALID_PAGE_TYPES.includes(pageType) ? pageType : "checkout";
  const initialOptions = {
    "client-id": clientId,
    ...(merchantId ? { "merchant-id": merchantId } : {}),
    ...(paypalPartnerAttributionId ? { "data-partner-attribution-id": paypalPartnerAttributionId } : {}),
    "data-page-type": dataPageType,
    ...(payLaterEnabled ? {} : { "disable-funding": "paylater" }),
    currency: "USD",
    intent: "capture",
    commit: true,
  };

  // Helper para extraer información del error de PayPal
  const extractPaypalError = (error) => {
    // PayPal SDK puede devolver el error de diferentes formas
    // Formato 1: error.details[0].issue (cuando viene del SDK directamente)
    // Formato 2: error.response.data.details[0].issue (cuando viene de una respuesta HTTP)
    // Formato 3: error.data.details[0].issue (formato alternativo)

    const errorDetails =
      error?.details?.[0] || error?.response?.details?.[0] || error?.data?.details?.[0] || error?.response?.data?.details?.[0] || {};
    const errorData = error?.data || error?.response?.data || {};

    // El código del error puede estar en 'issue' (dentro de details) o en 'name' (nivel superior)
    const errorCode = errorDetails.issue || errorData.name || error?.name || "PAYMENT_ERROR";

    return {
      code: errorCode,
      message: errorData.message || error?.message || "Payment failed",
      description: errorDetails.description || errorData.message || error?.message || "The payment could not be processed",
      debugId: errorData.debug_id || error?.debug_id || null,
    };
  };

  // Helper para obtener mensaje amigable al usuario
  const getUserFriendlyMessage = (errorCode, description) => {
    const messages = {
      PAYEE_NOT_CONSENTED: "The seller has not completed their PayPal setup. Please contact the seller to complete their payment configuration.",
      INSUFFICIENT_FUNDS: "Insufficient funds. PayPal was unable to process the payment. Please verify your payment method.",
      CARD_DECLINED: "Your card was declined by PayPal. Please try with another payment method.",
      PAYER_ACCOUNT_LOCKED_OR_CLOSED: "Your PayPal account is locked or closed. Please contact PayPal support to resolve this issue.",
      PAYER_CANNOT_PAY: "PayPal cannot process this payment. Please verify your PayPal account or try a different payment method.",
      TRANSACTION_REFUSED: "The transaction was refused by PayPal. Please try again or contact PayPal support.",
      NOT_AUTHORIZED: "Payment authorization failed. PayPal was unable to authorize this transaction. Please verify your payment information.",
    };

    return messages[errorCode] || description || "The payment could not be processed by PayPal. Please try again or contact support.";
  };

  useEffect(() => {
    if (!paypalPartnerAttributionId) {
      console.warn("PayPal partner attribution ID is not set");
    }
  }, [paypalPartnerAttributionId]);

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PayPalButtons
        style={{ layout: "vertical", shape: "rect" }}
        forceReRender={[orderId]}
        createOrder={() => orderId}
        onApprove={async (_data, actions) => {
          try {
            if (!actions?.order) return;

            const details = await actions.order.capture();
            console.log("Detalles de PayPal:", details);

            const capture = details?.purchase_units?.[0]?.payments?.captures?.[0];
            const status = capture?.status || details?.status;

            if (status === "COMPLETED") {
              onCaptureSuccess?.();
              handleChangeStatus?.(true);
            } else {
              handleChangeStatus?.(false);
            }
          } catch (err) {
            console.error("Error en onApprove:", err);
            // Extraer información del error de PayPal
            const errorInfo = extractPaypalError(err);
            const userFriendlyMessage = getUserFriendlyMessage(errorInfo.code, errorInfo.description);

            // Mostrar snackbar con mensaje amigable
            showSnackbar(userFriendlyMessage, "error", 10000, "top-center");

            handleChangeStatus?.(false);
          }
        }}
        onError={(err) => {
          console.error("Error en onError:", err);

          // También capturar errores en onError
          const errorInfo = extractPaypalError(err);
          const userFriendlyMessage = getUserFriendlyMessage(errorInfo.code, errorInfo.description);

          showSnackbar(userFriendlyMessage, "error", 10000, "top-center");

          handleChangeStatus?.(false);
        }}
      />
    </PayPalScriptProvider>
  );
};
